import React, { Fragment, useEffect, useState } from "react";
import { useAddZone, useEditZone } from "hooks/configuration/zone/zone";
import { Button, Row, Col, Form, Input, Select, message } from 'antd';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Image from 'helpers/Image';
import "../style.css";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";

const FormZone = () => {
    let navigate = useNavigate();
    const [form] = Form.useForm();
    const editData = useLocation();
    const [, setSelectedSite] = useState(null);
    const [serverErrors, setServerErrors] = useState({});
    const [firstLocation, setFirstLocation] = useState("");
    const [subCategories, setSubCategories] = useState([]);
    const [removeIds, setRemoveIds] = useState([]);

    const create = editData.pathname === "/configuration/zone/create";
    const locationSpan = subCategories.length + 1 < 3 ? 12 : 6;
    const status = [
        { label: "Active", value: true },
        { label: "Inactive", value: false },
    ];

    const { mutate: addZone, isLoading: Loading } = useAddZone();
    const { mutate: editZone, isLoading: editLoading } = useEditZone();

    const handleAddSubCategory = () => {
        setSubCategories(prev => [...prev, { id: null, name: '', isNew: true }]);
    };
    const handleDeleteSubCategory = (index) => {
        const deletedLocation = subCategories[index];
        setSubCategories(subCategories.filter((subCategory, i) => i !== index));
        if (deletedLocation && !deletedLocation.isNew && deletedLocation.id !== null) {
            setRemoveIds([...removeIds, deletedLocation.id]);
        }
    };

    const handleSubCategoryChange = (index, value) => {
        setSubCategories(
            subCategories.map((subCategory, i) =>
                i === index ? { ...subCategory, name: value } : subCategory
            )
        );
    };

    const handleFirstLocationChange = (e) => {
        setFirstLocation(e.target.value);
    };
    const onFinish = (formDatas) => {
        const siteNameTrimmed = (formDatas.siteName || "").trim();
        if (!siteNameTrimmed) {
            message.error("Site name required");
            return;
        }

        const firstLocTrimmed = firstLocation.trim();
        const additionalLocations = subCategories.map(sub => sub.name.trim()).filter(name => name.length > 0);
        const locationsArray = firstLocTrimmed ? [firstLocTrimmed, ...additionalLocations] : additionalLocations;

        if (locationsArray.length === 0) {
            message.error("Location required");
            return;
        }

        if (create) {
            const payload = {
                siteName: siteNameTrimmed,
                status: formDatas.status,
                locations: locationsArray,
            };

            addZone(payload, {
                onSuccess: (item) => {
                    if (item?.statusCode === 200) {
                        message.success(item?.message || item?.data);
                        navigate("/configuration/zone");
                    } else {
                        message.error(item?.message || item?.data);
                    }
                },
                onError: (error) => {
                    if (error?.response?.data?.errorMessages) {
                        const errorMessages = error.response.data.errorMessages;
                        setServerErrors(errorMessages);
                    } 
                },
            });
        } else {
            const locationsArrayObj = [];
            if (firstLocTrimmed) {
                locationsArrayObj.push({
                    locationId: editData.state.locationResDtoList[0]?.locationId || null,
                    locationName: firstLocTrimmed
                });
            }

            subCategories.forEach((sub) => {
                const trimmedName = sub.name.trim();
                if (trimmedName) {
                    locationsArrayObj.push({
                        locationId: sub.isNew ? 0 : sub.id,
                        locationName: trimmedName
                    });
                }
            });

            const payload = {
                siteId: formDatas.siteId,
                siteName: siteNameTrimmed,
                status: formDatas.status,
                locations: locationsArrayObj,
                removeIds: removeIds
            };

            editZone(payload, {
                onSuccess: (item) => {
                    if (item?.statusCode === 200) {
                        message.success(item?.message || item?.data);
                        navigate("/configuration/zone");
                    } else {
                        message.error(item?.message);
                    }
                },
                onError: (error) => {
                    if (error?.response?.data?.errorMessages) {
                        const errorMessages = error.response.data.errorMessages;
                        setServerErrors(errorMessages);
                    } 
                },
            });
        }
    };
    const clearError = (name) => {
        setServerErrors(prev => ({ ...prev, [name]: "" }));
    };


    const handleSiteChange = (value) => {
        setSelectedSite(value);
        form.setFieldsValue({ status: value });
    };

    useEffect(() => {
        if (!create && editData?.state) {
            form.setFieldsValue({
                siteId: editData.state.siteId,
                siteName: editData.state.siteName,
                status: editData.state.active,
            });

            if (editData.state.locationResDtoList && editData.state.locationResDtoList.length > 0) {
                setFirstLocation(editData.state.locationResDtoList[0]?.locationName || "");
                const remainingLocations = editData.state.locationResDtoList.slice(1).map((location) => ({
                    id: location.locationId,
                    name: location.locationName
                }));
                setSubCategories(remainingLocations);
            }
        }
    }, [editData, form, create]);

    return (
        <Fragment>
            <div className="form-page">
                <div className="form-head form-head-sticky">
                    <div className="form-head-left">
                        <Link to="/configuration/zone" className="form-back">
                            <Image name="backArrow.svg" />
                        </Link>
                        <div className="form-title">
                            <h1>{create ? "New Site" : "Edit Site"}</h1>
                            <p>{create ? "Add a site and the locations within it" : "Update this site and the locations within it"}</p>
                        </div>
                    </div>
                    <div className="form-actions">
                        <Button className="form-cancel" onClick={() => navigate("/configuration/zone")}>Cancel</Button>
                        <Button
                            className="form-submit"
                            loading={Loading || editLoading}
                            onClick={() => form.submit()}
                            htmlType="submit"
                        >
                            <Image className="save-img" name="save.svg" /> {create ? "Save" : "Update"}
                        </Button>
                    </div>
                </div>

                <div className="form-card">
                    <Form form={form} onFinish={onFinish} colon={false} layout="vertical">
                        <Form.Item name="siteId" hidden={true}>
                            <Input />
                        </Form.Item>

                        <section className="form-section">
                            <div className="form-section-head">
                                <h2>Site details</h2>
                                <p>The name this site is known by across the system.</p>
                            </div>
                            <Row gutter={[24, 0]}>
                                <Col xs={24} md={12}>
                                    <Form.Item label="Site Name" name="siteName" rules={[{ required: true, message: "Select the site name" }]} validateStatus={serverErrors?.siteName ? "error" : ""}
                                        help={serverErrors?.siteName?.[0]}>
                                        <Input placeholder="Enter the SiteName" maxLength={40} onChange={() => clearError("siteName")} />
                                    </Form.Item>
                                </Col>
                                {create ? null : (
                                    <Col xs={24} md={12}>
                                        <Form.Item label="Status" name="status" rules={[{ required: true, message: "Required!" }]}>
                                            <Select
                                                onChange={handleSiteChange}
                                                placeholder="Select status"
                                                options={status}
                                            />
                                        </Form.Item>
                                    </Col>
                                )}
                            </Row>
                        </section>

                        <section className="form-section">
                            <div className="form-section-head form-section-head-row">
                                <div>
                                    <h2>Locations</h2>
                                    <p>Points within this site that readers can be assigned to.</p>
                                </div>
                                <Button className="form-cancel" icon={<PlusOutlined />} onClick={handleAddSubCategory}>Add location</Button>
                            </div>
                            <Row gutter={[24, 0]}>
                                <Col xs={24} sm={12} lg={locationSpan}>
                                    <Form.Item label="Location 1" validateStatus={serverErrors?.CardId ? "error" : ""}
                                        help={serverErrors?.CardId?.[0]}>
                                        <Input
                                            value={firstLocation}
                                            onChange={handleFirstLocationChange}
                                            placeholder="Enter Location Name"
                                        />
                                    </Form.Item>
                                </Col>
                                {subCategories.map((subCategory, index) => (
                                    <Col xs={24} sm={12} lg={locationSpan} key={index}>
                                        <Form.Item label={`Location ${index + 2}`}>
                                            <div className="form-inline-row">
                                                <Input
                                                    value={subCategory.name}
                                                    onChange={(e) => handleSubCategoryChange(index, e.target.value)}
                                                    placeholder="Enter Location Name"
                                                />
                                                <Button
                                                    className="form-delete form-row-add"
                                                    icon={<DeleteOutlined />}
                                                    onClick={() => handleDeleteSubCategory(index)}
                                                />
                                            </div>
                                        </Form.Item>
                                    </Col>
                                ))}
                            </Row>
                        </section>
                    </Form>
                </div>
            </div>
        </Fragment>
    );
};

export default FormZone;

